import React, { useState, useEffect } from "react";
import { CiCircleRemove } from "react-icons/ci";
import styles from "./learningZoneUsersModal.module.css";
import axios from "axios";

const apiUrl = process.env.REACT_APP_URL;

const LearningZoneUsersModal = ({
  isOpen,
  onClose,
  learningZoneId,
  learningZoneName,
  lzCreatorUsername,
  isCreatorOrAdmin,
}) => {
  /* This component is used to view members of the learning zone. The creator can also use it to remove users from the learning zone (excluding the creator). */

  const [lzMembers, setLzMembers] = useState([]);
  const [userToRemove, setUserToRemove] = useState(null);

  /* Get the list of the learning zone members, from the database. */
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const res = await axios.get(
            `${apiUrl}/learning_zones/learning_zone_members/${learningZoneId}`,
            {
              withCredentials: true,
              params: {},
            }
          );

          if (res.data && res.data.success) {
            setLzMembers(res.data.members);
          }
        } catch (error) {
          console.error("Error fetching members:", error);
        }
      };

      fetchData();
    }
  }, [isOpen, learningZoneId]);

  /**
   * Handle user removal (confirmation message will be shown)
   * @param {*} username - the username to remove
   */
  const handleRemoveBtnClick = (username) => {
    setUserToRemove(username);
  };

  /**
   * Remove a user from the learning zone
   * @param {*} username - the username to remove
   */
  const handleConfirmRemove = async (username) => {
    try {
      await axios.delete(
        `${apiUrl}/learning_zones/list/remove/${learningZoneId}/${username}`,
        {
          withCredentials: true,
        }
      );

      setLzMembers((prevMembers) =>
        prevMembers.filter((member) => member.username !== username)
      );

      setUserToRemove(null);
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  /**
   * Handle cancel user removal (response to the confirmation message on removal)
   */
  const handleCancelRemove = () => {
    setUserToRemove(null);
  };

  /**
   * Close the modal when clicking outside it
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Visit a user's profile page, in a new tab.
   * @param {*} username - the username of the user to visit his / her profile page.
   */
  const visitProfile = (username) => {
    window.open(`/profile/${username}`, "_blank", "noopener,noreferrer");
  };

  if (!isOpen) return null;

  const BASE_URL = `${apiUrl}/`;

  return (
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>
        <h2>{learningZoneName} Members</h2>
        <div className={styles.membersList}>
          {lzMembers &&
            lzMembers.map((user, index) => (
              <figure key={index} className={styles.member}>
                {userToRemove === user.username ? (
                  <div className={styles.confirmation}>
                    <p>Are you sure?</p>
                    <button
                      className={styles.confirmButton}
                      onClick={() => handleConfirmRemove(user.username)}
                    >
                      Yes
                    </button>
                    <button
                      className={styles.cancelButton}
                      onClick={handleCancelRemove}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <>
                    {isCreatorOrAdmin &&
                      user.username !== lzCreatorUsername && (
                        <CiCircleRemove
                          className={styles.removeIcon}
                          onClick={() => handleRemoveBtnClick(user.username)}
                        />
                      )}
                    <img
                      src={`${BASE_URL}${user.img_link.replace(/\\/g, "/")}`}
                      alt={`${user.first_name} ${user.last_name}`}
                      className={styles.memberImage}
                      onClick={() => visitProfile(user.username)}
                    />
                    <figcaption
                      className={styles.memberInfo}
                      onClick={() => visitProfile(user.username)}
                    >
                      <p className={styles.memberUsername}>{user.username}</p>
                      {user.username === lzCreatorUsername && (
                        <p>Learning Zone Creator</p>
                      )}
                      <p className={styles.memberFullName}>
                        {user.first_name} {user.last_name}
                      </p>
                    </figcaption>
                  </>
                )}
              </figure>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LearningZoneUsersModal;
